// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import type { Org } from "@chotca/config";

// Profile-completion checker for the employer dashboard banner.
//
// Required org fields (name, address, contact…) are enforced at create time —
// tracking them here would just produce items that are always done. We focus
// on the *optional* but trust-building items: KYC verification + tax code.
// Logo/banner uploads will be added once the upload flow ships.

export type CompletionTaskKey = "kyc" | "taxCode";

export type CompletionTask = {
  key: CompletionTaskKey;
  done: boolean;
  href: string;
  /** True while the task is in a transient state (e.g. KYC pending review). */
  pending?: boolean;
};

export type CompletionSummary = {
  tasks: CompletionTask[];
  total: number;
  done: number;
  isComplete: boolean;
  /** KYC was reviewed and rejected — surface a dedicated banner. */
  kycRejected: boolean;
  kycRejectReason: string | null;
};

export function getCompletionSummary(org: Org | undefined): CompletionSummary {
  if (!org) {
    return {
      tasks: [],
      total: 0,
      done: 0,
      isComplete: false,
      kycRejected: false,
      kycRejectReason: null,
    };
  }

  const kycApproved = org.kycStatus === "APPROVED";
  const kycPending = org.kycStatus === "PENDING";
  const kycRejected = org.kycStatus === "REJECTED";

  // INDIVIDUAL (cá nhân) has no business paperwork — no org KYC, no tax code
  // (SCRUM-73/74). Only HOUSEHOLD / COMPANY need these trust-building tasks.
  const needsBusinessDocs = org.legalType !== "INDIVIDUAL";

  const tasks: CompletionTask[] = needsBusinessDocs
    ? [
        {
          key: "kyc",
          done: kycApproved,
          pending: kycPending,
          href: "/org/kyc",
        },
        {
          key: "taxCode",
          done: !!org.taxCode && org.taxCode.trim().length > 0,
          href: "/org",
        },
      ]
    : [];

  const done = tasks.filter((t) => t.done).length;
  return {
    tasks,
    total: tasks.length,
    done,
    isComplete: done === tasks.length,
    // INDIVIDUAL never submits org KYC, so it can't be in a rejected state.
    kycRejected: needsBusinessDocs && kycRejected,
    kycRejectReason:
      needsBusinessDocs && kycRejected ? (org.kycRejectReason ?? null) : null,
  };
}
